import type { FacilityWorkflowRunPayload } from '@Application/WorkflowRuns/FacilityWorkflowRuns/Actions/facilityWorkflowRunsFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import WorkflowRunFocusStepView from '@UI/Store/WorkflowRuns/Views/WorkflowRunFocusStepView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * One row of the supervision list. `subjectId`/`beneficiaryId`/`interviewId` are relation ids
 * only (`beneficiaryId` is null outside a beneficiary dossier): names are resolved by the directories.
 */
class FacilityWorkflowRunView {
	readonly id: number;
	readonly subjectId: number;
	readonly beneficiaryId: number | null;
	readonly interviewId: number | null;
	readonly label: string;
	readonly kind: LabeledValueView;
	readonly launchMode: LabeledValueView;
	readonly status: LabeledValueView;
	readonly startedAt: Date;
	readonly finishedAt: Date | null;
	readonly stepCount: number;
	readonly focusStep: WorkflowRunFocusStepView | null;
	readonly handledAt: Date | null;
	readonly handledById: number | null;
	readonly canChangeHandling: boolean;

	constructor(payload: FacilityWorkflowRunPayload) {
		this.id = payload.id;
		this.subjectId = payload.subject.id;
		this.beneficiaryId = payload.beneficiary?.id ?? null;
		this.interviewId = payload.interview?.id ?? null;
		this.label = payload.label;
		this.kind = new LabeledValueView(payload.kind);
		this.launchMode = new LabeledValueView(payload.launchMode);
		this.status = new LabeledValueView(payload.status);
		this.startedAt = new Date(payload.startedAt);
		this.finishedAt = payload.finishedAt !== null ? new Date(payload.finishedAt) : null;
		this.stepCount = payload.stepCount;
		this.focusStep = payload.focusStep !== null ? new WorkflowRunFocusStepView(payload.focusStep) : null;
		this.handledAt = payload.handledAt !== null ? new Date(payload.handledAt) : null;
		this.handledById = payload.handledBy?.id ?? null;
		this.canChangeHandling = payload.canChangeHandling;

		Object.freeze(this);
	}

	/** A proposed run opens on the preparation screen; an automatic one belongs to its interview. */
	get isProposal(): boolean {
		return this.launchMode.value === 'proposal';
	}
}

export default FacilityWorkflowRunView;
